import React, { useState, useRef, useEffect } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import logo from "./assets/logo-icon.svg";
import ReferralForm from "./components/ReferralForm";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AiFeaturesPage from "./pages/AiFeaturesPage";
import PaymentHistoryPage from "./pages/PaymentHistoryPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PaymentProvider, usePayment } from "./contexts/PaymentContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import UpgradeModal from "./components/UpgradeModal";
import PlanStatusBadge from "./components/PlanStatusBadge";
import HeaderLoginDropdown from "./components/HeaderLoginDropdown";
import "./App.css";

// Chrome extensions have no server, so BrowserRouter breaks (no URL handling).
// HashRouter uses #/path format which works in any static context.
const Router = process.env.REACT_APP_EXTENSION === "true" ? HashRouter : BrowserRouter;

function HeaderUserMenu({ user }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const initials = user.displayName
    ? user.displayName.charAt(0).toUpperCase()
    : user.email.charAt(0).toUpperCase();

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate("/");
  }

  return (
    <div className="header-user-menu" ref={menuRef}>
      <button
        className="header-avatar-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="User menu"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="header-avatar">{initials}</span>
        <span className="header-user-name">
          {user.displayName || user.email.split("@")[0]}
        </span>
        <span className={`header-chevron ${open ? "header-chevron--open" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="header-dropdown" role="menu">
          <div className="header-dropdown-info">
            <span className="header-dropdown-name">
              {user.displayName || "User"}
            </span>
            <span className="header-dropdown-email">{user.email}</span>
          </div>
          <hr className="header-dropdown-divider" />
          <button
            className="header-dropdown-item"
            role="menuitem"
            onClick={() => { setOpen(false); navigate("/payment-history"); }}
          >
            💳 Payment History
          </button>
          <hr className="header-dropdown-divider" />
          <button
            className="header-dropdown-item header-dropdown-logout"
            role="menuitem"
            onClick={handleLogout}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function MainLayout() {
  const { currentUser } = useAuth();
  const { triggerUpgrade } = usePayment();
  const { theme, toggleTheme } = useTheme();
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <img
              src={logo}
              alt="Referral Copilot"
              className="header-logo"
            />
            <div>
              <h1>✨ Referral Copilot</h1>
              <p className="app-header-subtitle">
                Get 3x More Replies on Your Referral Messages
              </p>
            </div>
          </div>

          <nav className="header-auth-nav" aria-label="Account navigation">
            <button
              className="header-theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>
            {currentUser ? (
              <>
                <button
                  className="header-pricing-btn"
                  onClick={() => triggerUpgrade({ message: "Choose a plan that works for you" })}
                >
                  💎 Pricing
                </button>
                <PlanStatusBadge />
                <HeaderUserMenu user={currentUser} />
              </>
            ) : (
              <>
                {/* Animated AI spark badge — clicking reveals the benefits page */}
                <Link
                  to="/ai-features"
                  className="header-ai-spark"
                  aria-label="Discover AI-powered features"
                >
                  <span className="header-ai-spark-icon" aria-hidden="true">⚡</span>
                  <span>Unlock AI</span>
                </Link>

                <div style={{ position: "relative" }}>
                  <button
                    className="header-btn header-btn--ghost"
                    onClick={() => setLoginOpen((v) => !v)}
                  >
                    Log In
                  </button>
                  {loginOpen && (
                    <HeaderLoginDropdown onClose={() => setLoginOpen(false)} />
                  )}
                </div>
                <Link to="/signup" className="header-btn header-btn--primary">
                  Sign Up
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Upgrade modal — triggered by 429 or badge click */}
      <UpgradeModal />

      {/* Main Content */}
      <main className="app-main">
        <div className="container-custom">
          <ReferralForm />
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="container-custom">
          <p>
            © 2025 Referral Copilot — Free LinkedIn Referral Message Generator. Built with ❤️ to help you get your dream job.
          </p>
          <p style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
            <a href="#privacy">Privacy</a> • <a href="#terms">Terms</a> •{" "}
            <a href="#contact">Contact Support</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <PaymentProvider>
          <Toaster position="top-right" toastOptions={{ style: { fontSize: "0.9rem" } }} />
          <Routes>
            <Route path="/" element={<MainLayout />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/ai-features" element={<AiFeaturesPage />} />
            <Route path="/payment-history" element={<PaymentHistoryPage />} />
          </Routes>
        </PaymentProvider>
      </AuthProvider>
    </ThemeProvider>
    </Router>
  );
}

export default App;